import { Button, FormControl, OutlinedInput, InputLabel, Stack, TextField, Autocomplete } from "@mui/material";
import Modal, { CommonModalProps } from "..";
import { forwardRef, useMemo, useState } from "react";
import { throttle } from "throttle-debounce";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { userService } from "../../../Core/Api/services";
import { IsEmail } from "../../../Utils/helpers";
import { DbMailingListInfos } from "../../../Core/Api/Types/db-mailing-list-infos";
import { produce, immerable } from "immer";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";

export type ModalMailingListElementProps = {
  context: "CREATE" | "UPDATE";
  ml?: DbMailingListInfos;
  onUpdate?: () => void;
};

export type ModalMailingListProps = CommonModalProps<ModalMailingListElementProps>;

const ModalMailingList = forwardRef<any, ModalMailingListProps>((props, ref) => {
  const { t } = useTranslation();
  const { context, ml, closeModal, onUpdate } = props;
  const [fetchedUsersEmails, setFetchedUsersEmails] = useState<string[]>([]);
  const [query, setQuery] = useState<string>("");
  const [mlist, setMlist] = useState<DbMailingListInfos>(ml || new DbMailingListInfos({ [immerable]: true }));
  const enqueueCustomSnackbar = useCustomSnackbar();

  const handleAutocompleteUsersMailInputChange = (searchValue: string) => {
    setQuery(searchValue);

    if (!searchValue || searchValue === "") return;

    throttledUpdateSearchEmail(searchValue);
  };

  const handleConfirmAction = () => {
    //here call
    context === "CREATE"
      ? userService.CreateMailingList(mlist).then((response) => {
          if (response.success) enqueueCustomSnackbar(t("alert:success.mailingListCreated"), "success");
        })
      : userService.UpdateMailingList(mlist).then((response) => {
          if (response.success) enqueueCustomSnackbar(t("alert:success.mailingListUpdated"), "success");
        });

    closeModal();
    onUpdate && onUpdate();
  };

  const throttledUpdateSearchEmail = useMemo(
    () =>
      throttle(
        GlobalConst.SEARCH_THROTTLE_DELAY_MS,
        (searchValue: string) => {
          userService.GetUsersEmailsFromSearh(searchValue).then((emails) => {
            setFetchedUsersEmails(emails);
          });
        },
        {
          noLeading: true,
        }
      ),
    []
  );

  return (
    <Modal
      title={context === "CREATE" ? t("modal:title.createMailingList") : t("modal:title.updateMailingList")}
      reference={ref}
      closeModal={closeModal}
      width={600}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={1.5}>
            <Button onClick={closeModal}>{t("common:cancel")}</Button>
          </Stack>
          <Button disabled={false} onClick={handleConfirmAction} variant="contained">
            {context === "CREATE" ? t("modal:title.createMailingList") : t("modal:title.updateMailingList")}
          </Button>
        </Stack>
      }
    >
      <FormControl sx={{ mb: 1 }}>
        <InputLabel shrink>{t("common:generalInformationsLowerCase")}</InputLabel>
        <OutlinedInput
          placeholder={t("common:name")}
          value={mlist?.name}
          onChange={(e) => {
            setMlist(
              produce((draft) => {
                draft.name = e.target.value;
              })
            );
          }}
        />
      </FormControl>

      <FormControl sx={{ mb: 1 }}>
        <InputLabel shrink>{t("button:createMailingList")}</InputLabel>
        <Autocomplete
          options={fetchedUsersEmails}
          onInputChange={(e, value) => handleAutocompleteUsersMailInputChange(value)}
          noOptionsText={t("field:helper.typeNameToFetchEmails")}
          inputValue={query}
          value={null}
          onChange={(e, value) => {
            if (value !== null) {
              setMlist(
                produce((draft) => {
                  draft.emails !== null && draft.emails?.length > 0
                    ? (draft.emails = [...(draft.emails as string[]), value.split(" ")[0]])
                    : (draft.emails = [value.split(" ")[0]]);
                })
              );
              setQuery("");
            }
          }}
          renderInput={(params) => (
            <TextField sx={{ mb: 2 }} {...params} placeholder={t("field:placeholder.searchMailingList")} />
          )}
        />
      </FormControl>

      <FormControl sx={{ mb: 1 }}>
        <InputLabel shrink>{t("field:label.addEmailManually")}</InputLabel>
        <Autocomplete
          options={[]}
          freeSolo
          multiple={true}
          onChange={(e, v) => {
            if (v.length > 0 && !IsEmail(v[v.length - 1])) return;
            setMlist(
              produce((draft) => {
                draft.emails = v.filter((x, i) => v.indexOf(x) === i);
              })
            );
          }}
          value={(mlist.emails as string[]) || []}
          renderInput={(params) => <TextField {...params} placeholder={t("field:placeholder.email")} />}
          getOptionLabel={(value: any) => value}
        />
      </FormControl>
    </Modal>
  );
});

export default ModalMailingList;
